import colors from '../components/colors';
import { css, ThemedCssFunction, StyledFunction, keyframes } from 'styled-components';
import responsiveToMedia from 'react-media-hoc';

interface MediaQuery {
    desktop: (...args: any[]) => ThemedCssFunction<any>;
    mobile: (...args: any[]) => ThemedCssFunction<any>;
}

const sizes = {
    desktop: { min: 767, max: 9999999 },
    mobile: { min: 0, max: 766.99 },
};

// Iterate through the sizes and create a media template
export const media = Object.keys(sizes).reduce((acc, label) => {
    acc[label] = (...args: any[]) => css`
        @media (max-width: ${sizes[label].max / 16}em) and (min-width: ${sizes[label].min / 16}em) {
            ${(css as any)(...args)};
        }
    `;
    return acc;
}, {}) as MediaQuery;

export const withMedia = (Component: any) =>
    responsiveToMedia({
        isDesktop: { minWidth: 767 },
        isMobile: { maxWidth: 766.99 },
    })(Component);

export interface WithMediaProps {
    isDesktop: boolean;
    isMobile: boolean;
}

export const baseFormStyles = `
    display: block;
    width: 100%;
    margin: 0;
    padding: 0.5rem;
    min-height: initial;

    border: 1px solid ${colors.mediumGray};
    border-radius: 0;
    background-color: ${colors.white};

    font-size: inherit;
    font-weight: inherit;

    transition: box-shadow 0.3s, border-color 0.25s ease-in-out, -webkit-box-shadow 0.5s;
    appearance: none;

    &:focus {
        outline: none;
        border: 1px solid ${colors.mediumGray};
        background-color: ${colors.white};
        box-shadow: 0 0 5px ${colors.mediumGray};
        transition: box-shadow 0.5s, border-color 0.25s ease-in-out, -webkit-box-shadow 0.5s;
    }

    &::placeholder {
        color: ${colors.mediumGray};
        opacity: 1;
    }

    ::-ms-input-placeholder {
        color: ${colors.mediumGray};
    }
`;

export const subtleScrollbarStyles = `
    /* Webkit Styles */
    ::-webkit-scrollbar {
        width: 3px;
        height: 3px;
    }
    ::-webkit-scrollbar-button {
        width: 0px;
        height: 0px;
    }
    ::-webkit-scrollbar-thumb {
        background: ${colors.mediumLightGray};
        border: 0px none #ffffff;
        border-radius: 50px;
    }
    ::-webkit-scrollbar-track {
        background: transparent;
        border: 0px none #ffffff;
        border-radius: 50px;
    }
    ::-webkit-scrollbar-corner {
        background: transparent;
    }
    /* IE Styles */
    scrollbar-base-color: transparent;
    scrollbar-face-color: ${colors.mediumLightGray};
    scrollbar-3dlight-color: transparent;
    scrollbar-highlight-color: transparent;
    scrollbar-track-color: transparent;
    scrollbar-arrow-color: white;
    scrollbar-shadow-color: transparent;
    scrollbar-dark-shadow-color: transparent;
`;

export function styledWithProps<T, U extends HTMLElement = HTMLElement>(
    styledFunction: StyledFunction<React.HTMLProps<U>>,
): StyledFunction<T & React.HTMLProps<U>> {
    return styledFunction;
}

const bounceIn = keyframes`
    0% {
        opacity: 0;
        transform: scale(0.3) translate3d(50px, 0, 0px) rotate(0deg);
    }
    50% {
        opacity: 0.9;
        transform: scale(1.05);
    }
    70% {
        opacity: 1;
        transform: scale(0.9);
    }
    100% {
        opacity: 1;
        transform: scale(1) translate3d(0,0,0) rotate(0deg);
    }
`;

const shakeSideways = keyframes`
    0% {
        transform: translate(0, 0);
    }
    .2%, 1.8% {
        transform: translate3d(-1px, 0, 0);
    }

    .4%, 1.6% {
        transform: translate3d(2px, 0, 0);
    }

    .6%, 1%, 1.4% {
        transform: translate3d(-4px, 0, 0);
    }

    .8%, 1.2% {
        transform: translate3d(4px, 0, 0);
    }
`;

const slideInY = keyframes`
    100% { transform: translateY(0%); }
`;

const fadeIn = keyframes`
    100% { opacity: 1 }
`;

const makeGrowIn = (height = 300) => keyframes`
    100% { max-height: ${height}px }
`;

export const circleCtaAnimation = `
    opacity: 0;
    animation: ${bounceIn} .5s linear, ${shakeSideways} 60s linear infinite 30s;
    animation-fill-mode: forwards;
`;

export const tabCtaAnimation = `
    transform: translateY(100%);
    animation: ${slideInY} .5s ease-in;
    animation-fill-mode: forwards;
`;

export const surveyAnimation = `
    max-height: 0;
    animation: ${makeGrowIn(160)} 1s ease-in;
    animation-fill-mode: forwards;
`;

export const proactiveAnimation = `
    opacity: 0;
    animation: ${fadeIn} .2s linear;
    animation-fill-mode: forwards;
`;



// WEBPACK FOOTER //
// ./~/tslint-loader!./src/util/styles.ts